import * as React from 'react';
import propz from 'propz';
import Moment from 'moment';
import {
  SchoolEvent,
  SchoolEventAttendance,
  SchoolEventIndividualData,
  SchoolEventTeamDataPlayer
} from '../../models/event';
import { getFullName } from 'Src/helpers/autocomplete/autocomplete';
import { AppUser } from 'Src/views/App/App';
import { getStudentAttendanceLog } from 'Src/helpers/service/admin/schoolEvents';
import './SchoolEventAttendanceSummary.scss';

interface Props {
  user: AppUser;
  schoolEvent: SchoolEvent;
  allTeamPlayers: (SchoolEventIndividualData | SchoolEventTeamDataPlayer)[];
  attendancePlayers: SchoolEventAttendance[];
  onChangeEventAttendanceClick: (value: boolean, player: SchoolEventIndividualData | SchoolEventTeamDataPlayer) => void;
  onChangeEventAllAttendanceClick: (value: boolean) => void;
  onChangeEventAttendanceComment: (
    field: 'publicComment' | 'internalComment',
    value: string,
    player: SchoolEventIndividualData | SchoolEventTeamDataPlayer
  ) => void;
  onEditAttendanceClick?: () => void;
  onCancelAttendanceClick?: () => void;
  onSaveAttendanceClick?: () => void;
  onOpenAttendanceLog?: () => void;
  isEditAttendance: boolean;
  isEditAllAttendance: boolean;
}

interface State {
  studentHistory: Record<string, any[]>;
  studentHistoryLoading: Record<string, boolean>;
  notesPlayer: (SchoolEventIndividualData | SchoolEventTeamDataPlayer) | null;
  fullHistoryStudent: { attendanceId: string; name: string } | null;
}

const ClipboardIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 2h6a1 1 0 0 1 1 1v2H8V3a1 1 0 0 1 1-1z" />
    <path d="M8 5H6a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
  </svg>
);

const NoteIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <path d="M8 8h8M8 12h5" />
  </svg>
);

const HistoryIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 4v4h4M12 7v5l3 2" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const getInitials = (name: string) => {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
};

export class SchoolEventAttendanceSummary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      studentHistory: {},
      studentHistoryLoading: {},
      notesPlayer: null,
      fullHistoryStudent: null
    };
  }

  getPlayerAttendance = (player: SchoolEventIndividualData | SchoolEventTeamDataPlayer) => {
    return this.props.attendancePlayers.find(a => a.userId === player.userId);
  };

  getPlayerAttendanceValue = (player: SchoolEventIndividualData | SchoolEventTeamDataPlayer) => {
    const attendance = this.getPlayerAttendance(player);
    return attendance && typeof attendance.isAttendance === 'boolean' ? attendance.isAttendance : undefined;
  };

  getAvatarUrl = (player: SchoolEventIndividualData | SchoolEventTeamDataPlayer) => {
    return propz.get(player, ['avatar'], '');
  };

  onOpenNotes = (player: SchoolEventIndividualData | SchoolEventTeamDataPlayer) => {
    this.setState({ notesPlayer: player });
  };

  onCloseNotes = () => {
    this.setState({ notesPlayer: null });
  };

  onOpenHistory = (player: SchoolEventIndividualData | SchoolEventTeamDataPlayer) => {
    const { user, schoolEvent } = this.props;
    const attendance = this.getPlayerAttendance(player);
    const name = getFullName(player);

    if (!attendance || !attendance._id) {
      this.setState({ fullHistoryStudent: { attendanceId: '', name } });
      return;
    }

    const attendanceId = attendance._id;

    this.setState({ fullHistoryStudent: { attendanceId, name } });

    if (this.state.studentHistory[attendanceId]) return;

    this.setState(prev => ({
      studentHistoryLoading: { ...prev.studentHistoryLoading, [attendanceId]: true }
    }));

    getStudentAttendanceLog(user, schoolEvent.id, attendanceId)
      .then(log => {
        this.setState(prev => ({
          studentHistory: { ...prev.studentHistory, [attendanceId]: log },
          studentHistoryLoading: { ...prev.studentHistoryLoading, [attendanceId]: false }
        }));
      })
      .catch(() => {
        this.setState(prev => ({
          studentHistoryLoading: { ...prev.studentHistoryLoading, [attendanceId]: false }
        }));
      });
  };

  onCloseFullHistory = () => {
    this.setState({ fullHistoryStudent: null });
  };

  renderAvatar = (player: SchoolEventIndividualData | SchoolEventTeamDataPlayer, name: string) => {
    const avatarUrl = this.getAvatarUrl(player);

    return (
      <span className="eAttendanceAvatar">
        {avatarUrl ? (
          <img className="eAttendanceAvatar__img" src={avatarUrl} alt="" />
        ) : (
          <span className="eAttendanceAvatar__fallback">{getInitials(name)}</span>
        )}
      </span>
    );
  };

  renderSegmentedStatus = (player: SchoolEventIndividualData | SchoolEventTeamDataPlayer) => {
    const { isEditAttendance, onChangeEventAttendanceClick } = this.props;
    const attendanceValue = this.getPlayerAttendanceValue(player);

    return (
      <div className="eAttendanceStatusToggle" aria-label="Attendance status">
        <button
          type="button"
          className={
            attendanceValue === true
              ? 'eAttendanceStatusToggle__option eAttendanceStatusToggle__option--present eAttendanceStatusToggle__option--active'
              : 'eAttendanceStatusToggle__option'
          }
          onClick={() => isEditAttendance && onChangeEventAttendanceClick(true, player)}
          disabled={!isEditAttendance}
        >
          Present
        </button>
        <button
          type="button"
          className={
            attendanceValue === false
              ? 'eAttendanceStatusToggle__option eAttendanceStatusToggle__option--absent eAttendanceStatusToggle__option--active'
              : 'eAttendanceStatusToggle__option'
          }
          onClick={() => isEditAttendance && onChangeEventAttendanceClick(false, player)}
          disabled={!isEditAttendance}
        >
          Absent
        </button>
      </div>
    );
  };

  renderHistoryEntry = (entry: any, index: number) => {
    const when = entry.createdAt ? Moment(entry.createdAt).format('DD MMM YYYY, HH:mm') : 'No date';
    const status = entry.isRemoved ? 'Removed' : entry.isAttendance ? 'Present' : 'Absent';
    const firstName = propz.get(entry, ['triggeredByUser', 'firstName'], '');
    const lastName = propz.get(entry, ['triggeredByUser', 'lastName'], '');
    const who = firstName || lastName ? `${firstName} ${lastName}`.trim() : 'System';

    return (
      <div key={index} className="eAttendanceHistoryEntry">
        <span
          className={
            entry.isRemoved
              ? 'eAttendanceHistoryStatus eRemoved'
              : entry.isAttendance
              ? 'eAttendanceHistoryStatus ePresent'
              : 'eAttendanceHistoryStatus eAbsent'
          }
        >
          {status}
        </span>
        <span className="eAttendanceHistoryMeta">
          {when} · {who}
        </span>
        {(entry.internalComment || entry.publicComment) && (
          <span className="eAttendanceHistoryNotes">
            {entry.internalComment && <span>Internal: {entry.internalComment}</span>}
            {entry.publicComment && <span>Public: {entry.publicComment}</span>}
          </span>
        )}
      </div>
    );
  };

  renderNotesModal = () => {
    const { notesPlayer } = this.state;
    const { isEditAttendance, onChangeEventAttendanceComment } = this.props;
    if (!notesPlayer) return null;

    const attendance = this.getPlayerAttendance(notesPlayer);
    const name = getFullName(notesPlayer);
    const internalComment = propz.get(attendance, ['internalComment'], '');
    const publicComment = propz.get(attendance, ['publicComment'], '');

    return (
      <div className="eAttendanceModalOverlay" onClick={this.onCloseNotes}>
        <div className="eAttendanceModal eAttendanceModal--notes" onClick={e => e.stopPropagation()}>
          <div className="eAttendanceModalHeader">
            <div className="eAttendanceModalTitle">
              <span className="eAttendanceModalTitle__icon">
                <NoteIcon />
              </span>
              <div>
                <h5>Attendance notes</h5>
                <div>{name}</div>
              </div>
            </div>
            <button className="eAttendanceIconButton" type="button" onClick={this.onCloseNotes} aria-label="Close">
              <CloseIcon />
            </button>
          </div>
          <div className="eAttendanceModalBody">
            <label className="eAttendanceNotesField">
              <span>Internal notes</span>
              <textarea
                value={internalComment}
                maxLength={500}
                rows={4}
                disabled={!isEditAttendance}
                onChange={e => onChangeEventAttendanceComment('internalComment', e.target.value, notesPlayer)}
              />
            </label>
            <label className="eAttendanceNotesField">
              <span>Public notes</span>
              <textarea
                value={publicComment}
                maxLength={500}
                rows={4}
                disabled={!isEditAttendance}
                onChange={e => onChangeEventAttendanceComment('publicComment', e.target.value, notesPlayer)}
              />
            </label>
          </div>
          <div className="eAttendanceModalFooter">
            <button type="button" className="sit-btn sit-btn--primary sit-btn--sm" onClick={this.onCloseNotes}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  renderFullHistoryModal = () => {
    const { fullHistoryStudent, studentHistory, studentHistoryLoading } = this.state;
    if (!fullHistoryStudent) return null;

    const attendanceId = fullHistoryStudent.attendanceId;
    const isLoading = Boolean(attendanceId && studentHistoryLoading[attendanceId]);
    const log = attendanceId ? studentHistory[attendanceId] || [] : [];

    return (
      <div className="eAttendanceModalOverlay" onClick={this.onCloseFullHistory}>
        <div className="eAttendanceModal" onClick={e => e.stopPropagation()}>
          <div className="eAttendanceModalHeader">
            <div className="eAttendanceModalTitle">
              <span className="eAttendanceModalTitle__icon">
                <HistoryIcon />
              </span>
              <div>
                <h5>Attendance log</h5>
                <div>{fullHistoryStudent.name}</div>
              </div>
            </div>
            <button
              className="eAttendanceIconButton"
              type="button"
              onClick={this.onCloseFullHistory}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="eAttendanceModalBody eAttendanceModalBody--history">
            {isLoading && <div className="eAttendanceHistoryEmpty">Loading log...</div>}
            {!isLoading && log.length === 0 && <div className="eAttendanceHistoryEmpty">No log entries yet.</div>}
            {!isLoading && log.map((entry, i) => this.renderHistoryEntry(entry, i))}
          </div>
        </div>
      </div>
    );
  };

  renderSummaryChip = (label: string, value: number, type: 'present' | 'absent' | 'total') => (
    <div className={`eAttendanceSummaryChip eAttendanceSummaryChip--${type}`}>
      <span className="eAttendanceSummaryChip__dot" />
      <span className="eAttendanceSummaryChip__label">{label}</span>
      <span className="eAttendanceSummaryChip__value">{value}</span>
    </div>
  );

  renderBulkActions = () => {
    const { allTeamPlayers, isEditAttendance, onChangeEventAllAttendanceClick } = this.props;
    if (!isEditAttendance) return null;

    const hasPlayers = allTeamPlayers.length > 0;
    const isAllPresent = hasPlayers && allTeamPlayers.every(player => this.getPlayerAttendanceValue(player) === true);
    const isAllAbsent = hasPlayers && allTeamPlayers.every(player => this.getPlayerAttendanceValue(player) === false);

    return (
      <div className="eAttendanceBulkActions" aria-label="Bulk attendance actions">
        <span>Set all</span>
        <button
          type="button"
          className={`eAttendanceBulkActions__button ePresent${isAllPresent ? ' is-active' : ''}`}
          onClick={() => onChangeEventAllAttendanceClick(true)}
          aria-pressed={isAllPresent}
        >
          Present
        </button>
        <button
          type="button"
          className={`eAttendanceBulkActions__button eAbsent${isAllAbsent ? ' is-active' : ''}`}
          onClick={() => onChangeEventAllAttendanceClick(false)}
          aria-pressed={isAllAbsent}
        >
          Absent
        </button>
      </div>
    );
  };

  renderHeaderActions = () => {
    const {
      allTeamPlayers,
      isEditAttendance,
      onEditAttendanceClick,
      onCancelAttendanceClick,
      onSaveAttendanceClick,
      onOpenAttendanceLog
    } = this.props;
    const isPlayersExist = allTeamPlayers.length > 0;
    const isEveryPlayerMarked = allTeamPlayers.every(
      player => typeof this.getPlayerAttendanceValue(player) === 'boolean'
    );

    if (isEditAttendance) {
      return (
        <div className="eAttendanceHeaderActions">
          <button type="button" className="sit-btn sit-btn--secondary sit-btn--sm" onClick={onCancelAttendanceClick}>
            Cancel
          </button>
          <button
            type="button"
            className="sit-btn sit-btn--primary sit-btn--sm"
            onClick={onSaveAttendanceClick}
            disabled={!isEveryPlayerMarked}
            title={!isEveryPlayerMarked ? 'Mark every student as present or absent before saving.' : undefined}
          >
            Save
          </button>
        </div>
      );
    }

    return (
      <div className="eAttendanceHeaderActions">
        {onOpenAttendanceLog && (
          <button
            type="button"
            className="eAttendanceChipButton eAttendanceChipButton--header"
            onClick={onOpenAttendanceLog}
          >
            <HistoryIcon />
            Event log
          </button>
        )}
        <button
          type="button"
          className="sit-btn sit-btn--primary sit-btn--sm"
          onClick={onEditAttendanceClick}
          disabled={!isPlayersExist}
        >
          Edit attendance
        </button>
      </div>
    );
  };

  render() {
    const { allTeamPlayers, isEditAttendance } = this.props;
    const total = allTeamPlayers.length;
    const present = allTeamPlayers.filter(player => this.getPlayerAttendanceValue(player) === true).length;
    const absent = allTeamPlayers.filter(player => this.getPlayerAttendanceValue(player) === false).length;

    return (
      <div className="eAttendance">
        <div className="eAttendanceHeader">
          <div>
            <h2 className="eAttendanceHeader__title">Attendance</h2>
            <div className="eAttendanceHeader__subtitle">Mark who attended and keep notes for the attendance log.</div>
          </div>
          {this.renderHeaderActions()}
        </div>

        {this.renderBulkActions()}

        <div className="eAttendanceSummary">
          {this.renderSummaryChip('Present', present, 'present')}
          {this.renderSummaryChip('Absent', absent, 'absent')}
          {this.renderSummaryChip('Students', total, 'total')}
        </div>

        <div className="eAttendanceList">
          {allTeamPlayers.length === 0 && <div className="eAttendanceEmpty">No participants</div>}
          {allTeamPlayers.map((player, index) => {
            const name = getFullName(player);
            const attendance = this.getPlayerAttendance(player);
            const hasNotes = Boolean(
              propz.get(attendance, ['internalComment'], '') || propz.get(attendance, ['publicComment'], '')
            );

            return (
              <div className="eAttendanceRow" key={`attendance_summary_${player.userId}_${index}`}>
                <div className="eAttendanceRow__student">
                  <span className="eAttendanceRow__index">{index + 1}</span>
                  {this.renderAvatar(player, name)}
                  <div className="eAttendanceRow__studentCopy">
                    <div className="eAttendanceRow__name">{name}</div>
                  </div>
                </div>

                <div className="eAttendanceRow__status">{this.renderSegmentedStatus(player)}</div>

                <div className="eAttendanceRow__actions">
                  <button
                    type="button"
                    className={
                      hasNotes ? 'eAttendanceChipButton eAttendanceChipButton--active' : 'eAttendanceChipButton'
                    }
                    onClick={() => this.onOpenNotes(player)}
                    disabled={!isEditAttendance && !hasNotes}
                  >
                    <NoteIcon />
                    Notes
                  </button>
                  <button type="button" className="eAttendanceChipButton" onClick={() => this.onOpenHistory(player)}>
                    <HistoryIcon />
                    Log
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {this.renderNotesModal()}
        {this.renderFullHistoryModal()}
      </div>
    );
  }
}
